import { Collapse, List } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { capitalizeFirstLetter } from '../../helpers/capitalizeFirstLetter';
import { SidebarListIcon, SidebarListItem, SidebarListItemText } from './style';
import { IRenderListItem } from './types';
import { getMenuItemPath, validateMenuItemPath } from './routingHelpers';

const RenderListItem: React.FC<IRenderListItem> = (props: IRenderListItem) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { index, item } = props;
  const section = item.routeParam;

  const hasSubItems = Boolean(item.subItems?.length);
  const isSectionActive = validateMenuItemPath(location.pathname, { section });
  const isActive = isSectionActive && !hasSubItems;
  const [isOpen, setIsOpen] = useState(isSectionActive);

  const handleToggleCollapse = () => {
    setIsOpen(!isOpen);
  };

  // Auto-open collapse if a subitem is active
  useEffect(() => {
    if (hasSubItems && item.subItems) {
      const hasActiveSubItem = item.subItems.some((subItem) => {
        const subsection = subItem.routeParam;
        return validateMenuItemPath(location.pathname, { section, subsection });
      });
      if (hasActiveSubItem) {
        setIsOpen(true);
      }
    }
  }, [location.pathname, hasSubItems, item.subItems, section]);

  return (
    <React.Fragment key={index}>
      <SidebarListItem
        isCollapseOpen={isOpen}
        isActive={isActive}
        disablePadding
        key={index}
        onClick={() => {
          if (hasSubItems) {
            handleToggleCollapse();
          } else {
            navigate(getMenuItemPath(section));
          }
        }}
      >
        <SidebarListIcon
          sx={{
            color: isActive ? 'common.white' : 'neutral.var70',
          }}
        >
          {item.icon}
        </SidebarListIcon>
        <SidebarListItemText variant="body1" isActive={isActive}>
          {capitalizeFirstLetter(item.name)}
        </SidebarListItemText>
        {hasSubItems &&
          (isOpen ? (
            <i
              className="fa-regular fa-chevron-up"
              style={{
                color: theme.palette.neutral.var70,
              }}
            />
          ) : (
            <i
              className="fa-regular fa-chevron-down fa-2xs"
              style={{
                color: theme.palette.neutral.var70,
              }}
            />
          ))}
      </SidebarListItem>
      {hasSubItems && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.subItems?.map((subItem) => {
              const subsection = subItem.routeParam;
              const isSubItemActive = validateMenuItemPath(location.pathname, { section, subsection });

              return (
                <SidebarListItem
                  key={subItem.key}
                  isActive={isSubItemActive}
                  onClick={() => {
                    navigate(getMenuItemPath(section, subsection));
                  }}
                  sx={{ pl: 4 }}
                >
                  <SidebarListItemText isActive={isSubItemActive}>{subItem.name}</SidebarListItemText>
                </SidebarListItem>
              );
            })}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );
};

export default RenderListItem;
